// src/table/AppClienteRecepcionReactTable.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import jwt_decode from "jwt-decode";
import styled from "styled-components";
import { useFilters, useSortBy, usePagination } from "react-table";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { Modal, Button, Table as BsTable, Spinner } from "react-bootstrap";

import ModalDocumentosClienteRecibe from "../components/ModalDocumentosClienteRecibe";
import ModalFormClienteRecepcion from "../components/ModalFormClienteRecepcion";
import Table from "./TableContainer";
import "../css/tablecomandas.css";

import inesLogo from "../images/ridersmiami-logo.png";

import {
  getClientesRecepcion,
  getClienteRecepcionById,
  deleteClienteRecepcion,
} from "../helpers/rutaClienterecepcion";

const Styles = styled.div`
  .table-container {
    overflow-x: auto;
    overflow-y: visible;
    height: auto;
    min-height: 0;
  }
`;

/* ------------------------------- helpers -------------------------------- */

function norm(v) {
  return (v ?? "")
    .toString()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim();
}

const digits = (v) => String(v ?? "").replace(/\D/g, "");

const clean = (v) => String(v ?? "").trim();

const getRoleUsuario = () => {
  const token = localStorage.getItem("token") || "";

  try {
    if (!token) return "";

    const decoded = jwt_decode(token);

    const usuario =
      decoded?.usuario ||
      decoded?.user ||
      decoded;

    return clean(usuario?.role).toUpperCase();
  } catch {
    return "";
  }
};

const fmtDate = (v) => {
  if (!v) return "-";

  const d = new Date(v);

  if (Number.isNaN(d.getTime())) return String(v);

  return d.toLocaleDateString("es-AR");
};

const textOrDash = (v) => {
  const s = String(v ?? "").trim();
  return s || "-";
};

const sanitizeFileName = (v) =>
  String(v ?? "")
    .trim()
    .replace(/[\\/:*?"<>|]+/g, "")
    .replace(/\s+/g, "_");

const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

const fitImageInBox = (img, maxW, maxH) => {
  const w = img?.naturalWidth || img?.width || maxW;
  const h = img?.naturalHeight || img?.height || maxH;
  const ratio = Math.min(maxW / w, maxH / h);

  return {
    w: w * ratio,
    h: h * ratio,
  };
};

const nombreCompletoRecepcion = (cliente = {}) => {
  const armado = [
    cliente?.nombre,
    cliente?.segundoNombre,
    cliente?.apellido1,
    cliente?.apellido2,
  ]
    .map(clean)
    .filter(Boolean)
    .join(" ")
    .replace(/\s+/g, " ")
    .trim();

  return armado || clean(cliente?.nombreCompleto) || "-";
};

const direccionCompletaRecepcion = (cliente = {}) => {
  const armado = [
    cliente?.direccion,
    cliente?.referenciaDireccion,
    cliente?.municipio,
    cliente?.provincia,
    cliente?.pais,
  ]
    .map(clean)
    .filter(Boolean)
    .join(" - ")
    .replace(/\s+/g, " ")
    .trim();

  return armado || clean(cliente?.direccionCompleta) || "-";
};

const getClienteFromResponse = (resp, fallback = null) => {
  if (resp?.clienterecepcion && !Array.isArray(resp.clienterecepcion)) {
    return resp.clienterecepcion;
  }

  if (resp?.cliente) return resp.cliente;

  if (resp?.data?.clienterecepcion && !Array.isArray(resp.data.clienterecepcion)) {
    return resp.data.clienterecepcion;
  }

  if (resp?.data?.cliente) return resp.data.cliente;
  if (resp?.data) return resp.data;

  return resp || fallback;
};

function uniqById(arr) {
  const m = new Map();

  for (const it of arr || []) {
    const k = it?._id ?? it?.codrecep ?? JSON.stringify(it);

    if (!m.has(k)) {
      m.set(k, it);
    }
  }

  return Array.from(m.values());
}

function useDebounce(value, delay = 250) {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);

  return debounced;
}

const btnActionSmall = {
  width: 28,
  height: 28,
  padding: 0,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: 4,
  fontSize: 13,
};

/* ------------------------------------------------------------------------ */

export default function AppClienteRecepcionReactTable() {

  const roleUsuario = getRoleUsuario();

  const puedeBorrar = [
    "ROOT_ROLE",
    "ADMIN_ROLE",
  ].includes(roleUsuario);

  const [showDocs, setShowDocs] = useState(false);
  const [clienteDocs, setClienteDocs] = useState(null);
  const [docsLoadingId, setDocsLoadingId] = useState(null);

  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showEdit, setShowEdit] = useState(false);
  const [clienteEdit, setClienteEdit] = useState(null);

  const [pdfLoadingId, setPdfLoadingId] = useState(null);

  const [showFicha, setShowFicha] = useState(false);
  const [fichaData, setFichaData] = useState(null);
  const [fichaLoadingId, setFichaLoadingId] = useState(null);

  const [q, setQ] = useState("");
  const dq = useDebounce(q, 250);

  const [progress, setProgress] = useState({
    loaded: 0,
    total: 0,
  });

  const seqRef = useRef(0);
  const abortRef = useRef(null);

  const fetchAll = useCallback(async () => {
    try {
      abortRef.current?.abort?.();
    } catch (_) {}

    const controller = new AbortController();
    abortRef.current = controller;

    const seq = ++seqRef.current;

    setLoading(true);
    setAll([]);
    setProgress({ loaded: 0, total: 0 });

    const LIMIT = 200;

    try {
      const first = await getClientesRecepcion({
        page: 1,
        limit: LIMIT,
        q: "",
        signal: controller.signal,
      });

      if (seq !== seqRef.current) return;

      const total = Number(first?.total || 0);
      let acc = [...(first?.clientes || first?.clienterecepcion || [])];

      acc = uniqById(acc);

      setAll(acc);
      setProgress({
        loaded: acc.length,
        total,
      });

      const pages = Math.max(1, Math.ceil(total / LIMIT));

      for (let p = 2; p <= pages; p++) {
        const r = await getClientesRecepcion({
          page: p,
          limit: LIMIT,
          q: "",
          signal: controller.signal,
        });

        if (seq !== seqRef.current) return;

        acc = uniqById(
          acc.concat(r?.clientes || r?.clienterecepcion || [])
        );

        setAll(acc);
        setProgress({
          loaded: acc.length,
          total,
        });
      }
    } catch (e) {
      if (e?.name === "CanceledError" || e?.code === "ERR_CANCELED") return;

      console.error("❌ Error cargando clientes recepción:", e);

      setAll([]);
      setProgress({
        loaded: 0,
        total: 0,
      });
    } finally {
      if (seq === seqRef.current) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    fetchAll();

    return () => {
      try {
        abortRef.current?.abort?.();
      } catch (_) {}
    };
  }, [fetchAll]);


  const abrirDocumentos = useCallback(async (clienteRow) => {
    if (!clienteRow?._id) {
      alert("No se encontró el ID del cliente que recibe.");
      return;
    }

    try {
      setDocsLoadingId(clienteRow._id);
      setClienteDocs(clienteRow);
      setShowDocs(true);

      const resp = await getClienteRecepcionById(clienteRow._id);
      const clienteCompleto = getClienteFromResponse(resp, clienteRow);

      setClienteDocs(clienteCompleto);
    } catch (e) {
      console.error("❌ Error cargando documentos del cliente que recibe:", e);
      setClienteDocs(clienteRow);
    } finally {
      setDocsLoadingId(null);
    }
  }, []);

  const cerrarDocumentos = useCallback(() => {
    setShowDocs(false);
    setClienteDocs(null);
  }, []);

  const actualizarClienteEnListado = useCallback((clienteActualizado) => {
    if (!clienteActualizado?._id) return;

    setAll((prev) =>
      (prev || []).map((c) =>
        c?._id === clienteActualizado._id ? clienteActualizado : c
      )
    );

    setClienteDocs(clienteActualizado);
  }, []);

  const borrar = useCallback(async (mongoId) => {
    if (!mongoId) {
      alert("No se encontró el ID del cliente de recepción.");
      return;
    }

    if (!window.confirm("¿Eliminar cliente de recepción?")) return;

    try {
      await deleteClienteRecepcion(mongoId);

      setAll((prev) => prev.filter((c) => c?._id !== mongoId));
    } catch (e) {
      console.error("❌ Error deleteClienteRecepcion:", e);
      alert("No se pudo borrar el cliente de recepción.");
    }
  }, []);

  const openEdicion = useCallback(async (cliente) => {
    const id = cliente?._id || cliente?.id;

    if (!id) {
      alert("No se encontró el ID del cliente de recepción.");
      return;
    }

    try {
      const resp = await getClienteRecepcionById(id);
      const cliCompleto = getClienteFromResponse(resp, cliente);

      setClienteEdit(cliCompleto);
      setShowEdit(true);
    } catch (error) {
      console.error("❌ Error buscando cliente recepción completo:", error);

      setClienteEdit(cliente);
      setShowEdit(true);
    }
  }, []);

  const cerrarEdicion = useCallback(() => {
    setShowEdit(false);
    setClienteEdit(null);
  }, []);

  const cargarFichaData = useCallback(async (clienteRow) => {
    const safe = async (label, fn, fallback) => {
      try {
        return await fn();
      } catch (e) {
        console.error(`❌ Error cargando ${label}:`, e);
        return fallback;
      }
    };

    const clienteResp = await safe(
      "cliente recepción por ID",
      () => getClienteRecepcionById(clienteRow._id),
      clienteRow
    );

    const cliente = getClienteFromResponse(clienteResp, clienteRow);
    const fechaEmision = new Date();

    return {
      cliente,
      fechaEmision,
    };
  }, []);

  const abrirFichaCliente = useCallback(
    async (clienteRow) => {
      if (!clienteRow?._id) {
        alert("No se encontró el ID del cliente de recepción.");
        return;
      }

      try {
        setFichaLoadingId(clienteRow._id);
        setFichaData(null);
        setShowFicha(true);

        const data = await cargarFichaData(clienteRow);
        setFichaData(data);
      } catch (e) {
        console.error("❌ Error abriendo ficha del cliente recepción:", e);

        setFichaData({
          cliente: clienteRow,
          fechaEmision: new Date(),
        });
      } finally {
        setFichaLoadingId(null);
      }
    },
    [cargarFichaData]
  );

  const generarPdfFicha = useCallback(
    async (clienteRow) => {
      if (!clienteRow?._id) return;

      try {
        setPdfLoadingId(clienteRow._id);

        const data = await cargarFichaData(clienteRow);

        const { cliente, fechaEmision } = data;

        const doc = new jsPDF({
          orientation: "p",
          unit: "mm",
          format: "a4",
        });

        const pageWidth = doc.internal.pageSize.getWidth();

        try {
          const logo = await loadImage(inesLogo);
          const box = fitImageInBox(logo, 34, 20);

          doc.addImage(logo, "PNG", 10, 7, box.w, box.h);
        } catch (e) {
          console.warn("No se pudo cargar el logo para el PDF:", e);
        }

        doc.setFont("times", "bold");
        doc.setFontSize(20);
        doc.text("FICHA DEL CLIENTE RECEPCIÓN", pageWidth / 2, 16, {
          align: "center",
        });

        doc.setFont("helvetica", "bold");
        doc.setFontSize(12);
        doc.text(`N°: ${textOrDash(cliente?.codrecep)}`, 190, 16, {
          align: "right",
        });

        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        doc.text(`Fecha emisión: ${fmtDate(fechaEmision)}`, 190, 22, {
          align: "right",
        });

        doc.setDrawColor(60);
        doc.setLineWidth(0.3);
        doc.line(10, 30, 200, 30);

        autoTable(doc, {
          startY: 34,
          theme: "grid",
          styles: {
            fontSize: 8.8,
            cellPadding: 2,
            valign: "middle",
            overflow: "linebreak",
          },
          columnStyles: {
            0: { cellWidth: 34, fontStyle: "bold" },
            1: { cellWidth: 61 },
            2: { cellWidth: 34, fontStyle: "bold" },
            3: { cellWidth: 61 },
          },
          body: [
            [
              "Código",
              textOrDash(cliente?.codrecep),
              "País",
              textOrDash(cliente?.pais || "Cuba"),
            ],
            [
              "Carnet Identidad",
              textOrDash(cliente?.carnetIdentidad),
              "Teléfono",
              textOrDash(cliente?.telefonoPrincipal),
            ],
            [
              "Nombre",
              textOrDash(cliente?.nombre),
              "Segundo nombre",
              textOrDash(cliente?.segundoNombre),
            ],
            [
              "Apellido 1",
              textOrDash(cliente?.apellido1),
              "Apellido 2",
              textOrDash(cliente?.apellido2),
            ],
            [
              "Nombre completo",
              textOrDash(nombreCompletoRecepcion(cliente)),
              "Email",
              textOrDash(cliente?.email),
            ],
            [
              "Dirección",
              textOrDash(cliente?.direccion),
              "Referencia",
              textOrDash(cliente?.referenciaDireccion),
            ],
            [
              "Provincia",
              textOrDash(cliente?.provincia),
              "Municipio",
              textOrDash(cliente?.municipio),
            ],
            [
              "Dirección completa",
              textOrDash(direccionCompletaRecepcion(cliente)),
              "",
              "",
            ],
          ],
        });

        doc.save(
          `Ficha_Cliente_Recepcion_${textOrDash(
            cliente?.codrecep
          )}_${sanitizeFileName(nombreCompletoRecepcion(cliente))}.pdf`
        );
      } catch (e) {
        console.error("❌ Error generando PDF de ficha recepción:", e);
        alert("No se pudo generar el PDF de la ficha del cliente recepción.");
      } finally {
        setPdfLoadingId(null);
      }
    },
    [cargarFichaData]
  );

  const dataFiltrada = useMemo(() => {
    const raw = String(dq ?? "").trim();

    if (!raw) return all;

    const hasLetters = /[a-zA-ZáéíóúñüÁÉÍÓÚÑÜ]/.test(raw);
    const qText = norm(raw);
    const qNum = digits(raw);

    return (all || []).filter((c) => {
      const cod = digits(c?.codrecep);
      const carnet = digits(c?.carnetIdentidad);
      const tel = digits(c?.telefonoPrincipal);

      const nombre = norm(nombreCompletoRecepcion(c));
      const email = norm(c?.email);
      const provincia = norm(c?.provincia);
      const municipio = norm(c?.municipio);
      const pais = norm(c?.pais);
      const direccion = norm(c?.direccion);
      const referencia = norm(c?.referenciaDireccion);
      const direccionCompleta = norm(direccionCompletaRecepcion(c));

      if (hasLetters) {
        return (
          nombre.includes(qText) ||
          email.includes(qText) ||
          provincia.includes(qText) ||
          municipio.includes(qText) ||
          pais.includes(qText) ||
          direccion.includes(qText) ||
          referencia.includes(qText) ||
          direccionCompleta.includes(qText)
        );
      }

      if (!qNum) return false;

      if (qNum.length >= 7) {
        return carnet === qNum || cod === qNum || tel === qNum;
      }

      return (
        carnet.startsWith(qNum) ||
        cod.startsWith(qNum) ||
        tel.startsWith(qNum)
      );
    });
  }, [all, dq]);

  const columns = useMemo(
    () => [
      {
        id: "editar",
        Header: "Edita",
        accessor: "_id",
        width: 50,
        Cell: ({ row }) => (
          <button
            className="action-btn action-btn--edit"
            title="Editar"
            onClick={() => openEdicion(row.original)}
            style={btnActionSmall}
          >
            <i className="fa fa-pencil" aria-hidden="true" />
          </button>
        ),
      },
      {
        id: "verFicha",
        Header: "Ver",
        accessor: "_id",
        width: 55,
        Cell: ({ row }) => (
          <button
            className="action-btn"
            title="Ver ficha del cliente recepción"
            onClick={() => abrirFichaCliente(row.original)}
            disabled={fichaLoadingId === row.original._id}
            style={{
              ...btnActionSmall,
              backgroundColor: "#1565c0",
              color: "#fff",
              border: "none",
              cursor:
                fichaLoadingId === row.original._id ? "not-allowed" : "pointer",
              opacity: fichaLoadingId === row.original._id ? 0.7 : 1,
            }}
          >
            <i className="fa fa-eye" aria-hidden="true" />
          </button>
        ),
      },
      {
        id: "pdf",
        Header: "PDF",
        accessor: "_id",
        width: 55,
        Cell: ({ row }) => (
          <button
            className="action-btn"
            title="Generar ficha PDF"
            onClick={() => generarPdfFicha(row.original)}
            disabled={pdfLoadingId === row.original._id}
            style={{
              ...btnActionSmall,
              backgroundColor: "#b71c1c",
              color: "#fff",
              border: "none",
              cursor:
                pdfLoadingId === row.original._id ? "not-allowed" : "pointer",
              opacity: pdfLoadingId === row.original._id ? 0.7 : 1,
            }}
          >
            <i className="fa fa-file-pdf-o" aria-hidden="true" />
          </button>
        ),
      },

      {
        id: "eliminar",
        Header: puedeBorrar ? "🗑️" : "",
        accessor: "_id",
        width: 50,
        Cell: ({ row }) =>
          puedeBorrar ? (
            <button
              className="action-btn action-btn--del"
              title="Eliminar"
              onClick={() => borrar(row.original._id)}
              style={btnActionSmall}
            >
              <i className="fa fa-trash-o" aria-hidden="true" />
            </button>
          ) : null,
      },

      {
        id: "docs",
        Header: "Docs",
        accessor: "_id",
        width: 60,
        Cell: ({ row }) => {
          const documentos = row.original?.documentos || {};
          const tieneDocs = !!(
            documentos?.frente?.url ||
            documentos?.dorso?.url ||
            documentos?.pdf?.url
          );

          return (
            <button
              className="action-btn"
              title={tieneDocs ? "Ver documentos cargados" : "Cargar documentos"}
              onClick={() => abrirDocumentos(row.original)}
              disabled={docsLoadingId === row.original._id}
              style={{
                width: 28,
                height: 28,
                padding: 0,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: 4,
                fontSize: 13,
                backgroundColor: tieneDocs ? "#2e7d32" : "#6c757d",
                color: "#fff",
                border: "none",
                cursor:
                  docsLoadingId === row.original._id ? "not-allowed" : "pointer",
                opacity: docsLoadingId === row.original._id ? 0.7 : 1,
              }}
            >
              <i
                className={`fa ${tieneDocs ? "fa-id-card-o" : "fa-paperclip"}`}
                aria-hidden="true"
              />
            </button>
          );
        },
      },

      {
        Header: "Código",
        accessor: "codrecep",
        width: 70,
        minWidth: 55,
        maxWidth: 90,
      },
      {
        Header: "Carnet",
        accessor: "carnetIdentidad",
        width: 150,
        minWidth: 120,
      },
      {
        Header: "Cliente recepción",
        accessor: (row) => nombreCompletoRecepcion(row),
        id: "clienteRecepcionNombre",
        width: 320,
        minWidth: 220,
        Cell: ({ value }) => (
          <div
            style={{
              textAlign: "left",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
            title={value}
          >
            {value}
          </div>
        ),
      },
      {
        Header: "Teléfono",
        accessor: "telefonoPrincipal",
        width: 130,
        minWidth: 100,
        maxWidth: 170,
      },
      {
        Header: "Email",
        accessor: "email",
        width: 220,
        minWidth: 180,
        Cell: ({ value }) => (
          <div
            style={{
              textAlign: "left",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
            title={value || ""}
          >
            {value || "-"}
          </div>
        ),
      },
      {
        Header: "Provincia",
        accessor: "provincia",
        width: 150,
        minWidth: 110,
      },
      {
        Header: "Municipio",
        accessor: "municipio",
        width: 150,
        minWidth: 110,
      },
      {
        Header: "Dirección",
        accessor: (row) => direccionCompletaRecepcion(row),
        id: "direccionCompleta",
        width: 360,
        minWidth: 260,
        Cell: ({ value }) => (
          <div
            style={{
              textAlign: "left",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
            title={value}
          >
            {value}
          </div>
        ),
      },
    ],
    [
      openEdicion,
      abrirFichaCliente,
      borrar,
      generarPdfFicha,
      fichaLoadingId,
      pdfLoadingId,
      puedeBorrar,
    ]
  );

  const tableKey = useMemo(() => `${dq}__${all.length}`, [dq, all.length]);

  return (
    <div className="container mt-4">
      <h3 className="text-center mb-2">
        LISTADO DE CLIENTES QUE RECEPCIONAN EN CUBA
      </h3>

      {/* ---------------- BUSCADOR ---------------- */}
      <div className="container mb-2">
        <div className="d-flex align-items-end justify-content-between gap-3 flex-wrap">
          <div className="d-flex align-items-end gap-3 flex-wrap">
            <div style={{ minWidth: 380 }}>
              <label className="form-label mb-0 small">Buscar</label>
              <input
                className="form-control form-control-sm"
                placeholder="nombre / carnet / teléfono / email / provincia / municipio / dirección"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
            </div>

            <div
              className="small text-muted mb-1"
              style={{ whiteSpace: "nowrap" }}
            >
              {loading
                ? `Cargando ${progress.loaded} / ${progress.total || "?"}...`
                : `Mostrando ${dataFiltrada.length} de ${all.length}`}
            </div>
          </div>

          <div className="d-flex gap-2">
            <button
              className="btn btn-outline-secondary btn-sm px-3"
              style={{ fontSize: 14 }}
              onClick={() => setQ("")}
            >
              Limpiar
            </button>

            <button
              className="btn btn-outline-primary btn-sm px-3"
              style={{ fontSize: 14 }}
              onClick={fetchAll}
              disabled={loading}
            >
              Recargar
            </button>
          </div>
        </div>
      </div>

      {/* ---------------- TABLA ---------------- */}
      <Styles>
        <div className="table-container">
          <Table
            key={tableKey}
            columns={columns}
            data={dataFiltrada}
            loading={loading}
            plugins={[useFilters, useSortBy, usePagination]}
          />
        </div>
      </Styles>

      {/* ---------------- MODAL EDICIÓN ---------------- */}
      <ModalFormClienteRecepcion
        show={showEdit}
        cliente={clienteEdit}
        handleClose={cerrarEdicion}
        onSaved={fetchAll}
      />

      {/* ---------------- MODAL DOCS ---------------- */}
      <ModalDocumentosClienteRecibe
        show={showDocs}
        cliente={clienteDocs}
        onHide={cerrarDocumentos}
        onClienteActualizado={actualizarClienteEnListado}
      />

      {/* ---------------- MODAL FICHA CLIENTE RECEPCIÓN ---------------- */}
      <Modal
        show={showFicha}
        onHide={() => setShowFicha(false)}
        size="xl"
        centered
        scrollable
      >
        <Modal.Header closeButton>
          <Modal.Title>Ficha del Cliente Recepción</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {fichaLoadingId && !fichaData ? (
            <div className="text-center py-5">
              <Spinner animation="border" />
              <div className="mt-2">Cargando ficha...</div>
            </div>
          ) : fichaData ? (
            <>
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  borderBottom: "1px solid #333",
                  paddingBottom: 12,
                  marginBottom: 16,
                }}
              >
                <div style={{ width: 160 }}>
                  <img
                    src={inesLogo}
                    alt="Logo"
                    style={{
                      maxWidth: 130,
                      maxHeight: 55,
                      objectFit: "contain",
                    }}
                  />
                </div>

                <div
                  style={{
                    flex: 1,
                    textAlign: "center",
                    fontFamily: "serif",
                    fontWeight: "bold",
                    fontSize: 28,
                  }}
                >
                  FICHA DEL CLIENTE RECEPCIÓN
                </div>

                <div style={{ width: 170, textAlign: "right" }}>
                  <div style={{ fontWeight: "bold", fontSize: 18 }}>
                    N°: {textOrDash(fichaData.cliente?.codrecep)}
                  </div>

                  <div style={{ fontSize: 13, marginTop: 8 }}>
                    Fecha emisión: {fmtDate(fichaData.fechaEmision)}
                  </div>
                </div>
              </div>

              <BsTable bordered size="sm" responsive style={{ fontSize: 13 }}>
                <tbody>
                  <tr>
                    <th style={{ width: "15%" }}>Código</th>
                    <td style={{ width: "35%" }}>
                      {textOrDash(fichaData.cliente?.codrecep)}
                    </td>

                    <th style={{ width: "15%" }}>Carnet Identidad</th>
                    <td style={{ width: "35%" }}>
                      {textOrDash(fichaData.cliente?.carnetIdentidad)}
                    </td>
                  </tr>

                  <tr>
                    <th>Nombre</th>
                    <td>{textOrDash(fichaData.cliente?.nombre)}</td>

                    <th>Segundo nombre</th>
                    <td>{textOrDash(fichaData.cliente?.segundoNombre)}</td>
                  </tr>

                  <tr>
                    <th>Apellido 1</th>
                    <td>{textOrDash(fichaData.cliente?.apellido1)}</td>

                    <th>Apellido 2</th>
                    <td>{textOrDash(fichaData.cliente?.apellido2)}</td>
                  </tr>

                  <tr>
                    <th>Nombre completo</th>
                    <td>
                      {textOrDash(nombreCompletoRecepcion(fichaData.cliente))}
                    </td>

                    <th>País</th>
                    <td>{textOrDash(fichaData.cliente?.pais || "Cuba")}</td>
                  </tr>

                  <tr>
                    <th>Teléfono</th>
                    <td>{textOrDash(fichaData.cliente?.telefonoPrincipal)}</td>

                    <th>Email</th>
                    <td>{textOrDash(fichaData.cliente?.email)}</td>
                  </tr>

                  <tr>
                    <th>Dirección</th>
                    <td>{textOrDash(fichaData.cliente?.direccion)}</td>

                    <th>Referencia</th>
                    <td>{textOrDash(fichaData.cliente?.referenciaDireccion)}</td>
                  </tr>

                  <tr>
                    <th>Provincia</th>
                    <td>{textOrDash(fichaData.cliente?.provincia)}</td>

                    <th>Municipio</th>
                    <td>{textOrDash(fichaData.cliente?.municipio)}</td>
                  </tr>

                  <tr>
                    <th>Dirección completa</th>
                    <td colSpan={3}>
                      {textOrDash(direccionCompletaRecepcion(fichaData.cliente))}
                    </td>
                  </tr>
                </tbody>
              </BsTable>
            </>
          ) : (
            <div className="text-muted">Sin datos para mostrar.</div>
          )}
        </Modal.Body>

        <Modal.Footer>
          {fichaData?.cliente && (
            <Button
              variant="danger"
              onClick={() => generarPdfFicha(fichaData.cliente)}
              disabled={pdfLoadingId === fichaData.cliente?._id}
            >
              PDF
            </Button>
          )}

          <Button variant="secondary" onClick={() => setShowFicha(false)}>
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}